'use client';

import type { FormEvent, MouseEvent, ReactNode } from 'react';
import { CuteSpinner } from '@/components/common/CuteSpinner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface SummarizerOptionsDialogProperties {
  cancelLabel?: string | undefined;
  chatLabel?: string | undefined;
  chatLoadingLabel?: string | undefined;
  children?: ReactNode;
  description?: string | null | undefined;
  isChatDisabled?: boolean | undefined;
  isChatLoading?: boolean | undefined;
  isSubmitDisabled?: boolean | undefined;
  isSummarizing?: boolean | undefined;
  onCancel: () => void;
  onChat?: ((event: MouseEvent<HTMLButtonElement>) => void) | undefined;
  onOpenChange: (open: boolean) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  open: boolean;
  submitLabel?: string | undefined;
  title?: string | undefined;
}

export function SummarizerOptionsDialog({
  open,
  onOpenChange,
  isSummarizing = false,
  title = 'Summarize',
  description = 'Adjust the summarizer before running it.',
  onSubmit,
  onCancel,
  submitLabel = 'Summarize',
  cancelLabel = 'Cancel',
  children,
  isSubmitDisabled = false,
  onChat,
  isChatDisabled = false,
  isChatLoading = false,
  chatLabel = 'Chat',
  chatLoadingLabel = 'Chatting…',
}: SummarizerOptionsDialogProperties) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form
          className="space-y-6"
          onSubmit={(event) => {
            event.preventDefault();
            onSubmit(event);
          }}
        >
          <DialogHeader className="space-y-2.5">
            <DialogTitle>{title}</DialogTitle>
            {description ? <DialogDescription>{description}</DialogDescription> : null}
          </DialogHeader>

          {children == null ? null : <div className="space-y-4">{children}</div>}

          <DialogFooter className="flex flex-wrap items-center justify-end gap-2 !flex-row">
            <Button type="button" variant="outline" onClick={onCancel}>
              {cancelLabel}
            </Button>
            {onChat ? (
              <Button type="button" variant="secondary" onClick={onChat} disabled={isChatDisabled || isChatLoading}>
                {isChatLoading ? (
                  <>
                    <CuteSpinner size={16} className="mr-2 border-current/40 border-t-current" aria-hidden="true" />
                    {chatLoadingLabel}
                  </>
                ) : (
                  chatLabel
                )}
              </Button>
            ) : null}
            <Button type="submit" disabled={isSummarizing || isSubmitDisabled}>
              {submitLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default SummarizerOptionsDialog;
